import { EditableText, InRow, Segmented } from "@countdown/door-editor";
import {
  CompletionForm,
  type FormBlock,
  type FormTextKey,
  type ParticipantEdit,
} from "@countdown/theme";
import { GripVertical } from "lucide-react";
import { type ReactElement, type ReactNode, useEffect, useRef, useState } from "react";

import { ConsentWordingEditor } from "@/components/ConsentWordingEditor";
import { HelpTip } from "@/components/HelpTip";
import { ParticipantFrame } from "@/components/ParticipantFrame";
import { EditorLayout } from "@/components/PreviewLayout";
import { type SaveResult, useSaveSection } from "@/components/SaveBar";
import { SaveForm } from "@/components/SaveForm";
import { Button } from "@/components/ui/button";
import {
  type ActionDetail,
  type ConsentTemplate,
  type ConsentType,
  LOAD_FAILED,
  client,
  loadActionDetail,
  loadConsentTemplates,
  saveActionSection,
  saveLeadFields,
  useResource,
} from "@/lib/api";
import { sameValue } from "@/lib/calendar-draft";
import { HELP_TIPS } from "@/lib/help";
import { CONSENT_TYPE_LABELS } from "@/lib/labels";
import { type Wording, wordingOf } from "@/lib/wording";
import {
  type FieldRow,
  type FieldType,
  MODE_LABELS,
  type Mode,
  type RegistrationForm,
  TYPE_LABELS,
  asksForAddress,
  asksForFreeText,
  formOf,
  modesFor,
  movedBlock,
  newField,
  previewFields,
  requestOf,
  withType,
} from "@/lib/registration";
import { cn } from "@/lib/utils";

const FIELD_CLASS = "mt-1 w-full border border-input bg-background px-3 py-2 text-sm";
const LABEL_CLASS = "mt-3 block text-sm font-medium";
const NOTE_CLASS = "mt-3 text-sm text-muted-foreground";
const CHOSEN_CLASS = "cursor-pointer outline-dashed outline-1 outline-offset-4 outline-primary";
const CHOOSABLE_CLASS =
  "cursor-pointer outline-dashed outline-1 outline-offset-4 outline-transparent hover:outline-primary/50";

function addressHint(wording: Wording): string {
  return `Adressdaten erst abfragen, wenn ein ${wording.nominative} sie wirklich braucht.`;
}
const FREE_TEXT_HINT =
  "Freitext kann Gesundheitsangaben anziehen. Fragen Sie nur ab, was die Aktion braucht.";

/** The page's own title, shown while the text is empty (`abschluss.astro`). */
const PAGE_DEFAULT = "Teilnahme abschließen";

type TextField =
  | "title"
  | "headline"
  | "intro"
  | "submitLabel"
  | "emailLabel"
  | "emailHint"
  | "termsLabel"
  | "accountTitle"
  | "accountHint";

/** Each text of the form: where the editor holds it, its name and the server's limit. */
const TEXTS: Readonly<
  Record<FormTextKey, { readonly field: TextField; readonly label: string; readonly max: number }>
> = {
  title: { field: "title", label: "Seitentitel", max: 80 },
  headline: { field: "headline", label: "Überschrift", max: 120 },
  intro: { field: "intro", label: "Einleitung", max: 600 },
  submit_label: { field: "submitLabel", label: "Absendeknopf", max: 40 },
  email_label: { field: "emailLabel", label: "Beschriftung der E-Mail-Adresse", max: 80 },
  email_hint: { field: "emailHint", label: "Hinweis zur E-Mail-Adresse", max: 300 },
  terms_label: { field: "termsLabel", label: "Zeile der Teilnahmebedingungen", max: 200 },
  account_title: { field: "accountTitle", label: "Kundenkonto", max: 80 },
  account_hint: { field: "accountHint", label: "Hinweis zum Kundenkonto", max: 400 },
};

/**
 * The elements of the form as the editor names them, in the bar and over their settings;
 * participants never read these words (D52: no titles on the form).
 */
const BLOCK_LABELS: Readonly<Record<FormBlock, string>> = {
  email: "E-Mail-Adresse",
  lead_fields: "Felder",
  terms: "Teilnahmebedingungen",
  registration: "Teilnahme-Einwilligung",
  account: "Kundenkonto",
  newsletter: "Newsletter",
  sponsor_contact: "Sponsorenkontakt",
  daily_reminder: "Tagesmail",
};

/** A block that is one consent kind (D51); the blocks are named like the kinds. */
function consentOf(block: FormBlock): ConsentType | null {
  return block === "registration" ||
    block === "newsletter" ||
    block === "sponsor_contact" ||
    block === "daily_reminder"
    ? block
    : null;
}

/** Element ids: `formular-<part>`, or `formular-<entry>-<part>` for a block or a field. */
/**
 * Why a consent the form asks for has no text in the preview. `known`: the template list is
 * readable (tenant admin), so a kind without approved wording can be told from a draft that
 * still lacks the organizer or privacy link.
 */
function missingWording(known: boolean, approved: boolean): string {
  if (known && !approved) {
    return "Es fehlt ein freigegebener Wortlaut.";
  }
  return approved
    ? "Der Wortlaut erscheint, sobald Veranstalter und Datenschutzhinweis eingetragen sind."
    : "Der Wortlaut erscheint, sobald Veranstalter und Datenschutzhinweis eingetragen sind und ein Wortlaut freigegeben ist.";
}

function id(part: string, row?: string): string {
  return row === undefined ? `formular-${part}` : `formular-${row}-${part}`;
}

/** Announced when it appears: the region stands on the page before its text does. */
function Hint({ text }: { readonly text: string | null }): ReactElement {
  return <div role="status">{text === null ? null : <p className={NOTE_CLASS}>{text}</p>}</div>;
}

/**
 * One element of the bar: its name chooses it, the second click goes back to the page. A
 * switched-off element stands dashed: listed, so it can be chosen and switched on, but not on the
 * form.
 */
function Entry({
  entry,
  name,
  off = false,
  chosen,
  onChoose,
}: {
  readonly entry: string;
  readonly name: string;
  readonly off?: boolean;
  readonly chosen: boolean;
  readonly onChoose: () => void;
}): ReactElement {
  return (
    <li>
      <Button
        id={id("waehlen", entry)}
        type="button"
        size="sm"
        variant={chosen ? "default" : "outline"}
        className={off && !chosen ? "border-dashed text-muted-foreground" : undefined}
        aria-current={chosen ? "true" : undefined}
        onClick={onChoose}
      >
        {name}
        {off ? <span className="sr-only">, aus</span> : null}
      </Button>
    </li>
  );
}

/**
 * One movable part of the form in the preview (D37, form part; D52; 21.10.2026): a block, or a
 * field inside »Felder«. Its handle is the one way to move it: the mouse drags it onto another
 * part of the same kind, the arrow keys move it one place, the keyboard's way and the phone's,
 * where dragging is unreliable. The handle shows under the mouse, with the focus and on the
 * chosen part; a block's stands left, a field's right, so the two never cover each other. Native
 * controls only, as everywhere in the frame.
 */
function Movable({
  kind,
  block,
  name,
  handle,
  chosen,
  dragging,
  disabled,
  onDragStart,
  onDragEnd,
  onDrop,
  onMove,
  children,
}: {
  readonly kind: "block" | "field";
  /** The block this part is, for the preview's click (`data-block`). */
  readonly block?: FormBlock;
  /** What the handle moves, in its name: "Newsletter verschieben". */
  readonly name: string;
  readonly handle: string;
  readonly chosen: boolean;
  /** A part of the same kind is being dragged, so this one is a drop target. */
  readonly dragging: boolean;
  readonly disabled: boolean;
  readonly onDragStart: () => void;
  readonly onDragEnd: () => void;
  readonly onDrop: () => void;
  readonly onMove: (step: -1 | 1) => void;
  readonly children: ReactNode;
}): ReactElement {
  const [over, setOver] = useState(false);
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- a drop target for the mouse; the keyboard moves the part with its handle
    <div
      data-block={block}
      className={cn(
        "relative",
        kind === "block" ? "group/block" : "group/field",
        kind === "block" ? (over || chosen ? CHOSEN_CLASS : CHOOSABLE_CLASS) : over ? CHOSEN_CLASS : undefined,
      )}
      onDragOver={(event) => {
        if (dragging) {
          event.preventDefault();
          event.stopPropagation();
          setOver(true);
        }
      }}
      onDragLeave={() => {
        setOver(false);
      }}
      onDrop={(event) => {
        if (!dragging) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        setOver(false);
        onDrop();
      }}
    >
      <button
        id={handle}
        type="button"
        draggable={!disabled}
        disabled={disabled}
        className={cn(
          "absolute top-0 inline-flex size-6 cursor-grab items-center justify-center rounded-md border-0 bg-transparent p-0 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:opacity-100",
          kind === "block" ? "-left-7" : "-right-7",
          chosen
            ? undefined
            : kind === "block"
              ? "opacity-0 group-hover/block:opacity-100"
              : "opacity-0 group-hover/field:opacity-100",
        )}
        aria-label={`${name} verschieben`}
        title="Ziehen oder mit den Pfeiltasten verschieben"
        onKeyDown={(event) => {
          if (event.key === "ArrowUp" || event.key === "ArrowDown") {
            event.preventDefault();
            onMove(event.key === "ArrowUp" ? -1 : 1);
          }
        }}
        onDragStart={(event) => {
          event.stopPropagation();
          // Firefox starts no drag without data.
          event.dataTransfer?.setData("text/plain", handle);
          if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = "move";
          }
          onDragStart();
        }}
        onDragEnd={onDragEnd}
      >
        <GripVertical className="size-4" aria-hidden="true" />
      </button>
      {children}
    </div>
  );
}

/**
 * The platform's fields keep their own label where one was emptied: the page needs a label,
 * and the server refuses a field without one.
 */
function withDefaultLabels(form: RegistrationForm, titles: ReadonlyMap<string, string>): RegistrationForm {
  return {
    ...form,
    fields: form.fields.map((row) =>
      row.type === null && row.label.trim() === ""
        ? { ...row, label: titles.get(row.key) ?? row.key }
        : row,
    ),
  };
}

/**
 * The untouched proposal (first and last name) of a new action, outside the design tab's form
 * view: in the settings, where an action goes live. Without it a form saved nowhere asks only for
 * the address. It joins that tab's bar as something to save, as in the form view, and says so.
 */
export function FormProposal({
  detail,
  onSaved,
}: {
  readonly detail: ActionDetail;
  readonly onSaved: () => void;
}): ReactElement | null {
  const [taken, setTaken] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const ended = detail.status === "finished" || detail.status === "archived";
  const proposed = !taken && !ended && detail.lead_fields_editable && formOf(detail).proposed;

  async function save(): Promise<SaveResult> {
    const outcome = await saveLeadFields(requestOf(formOf(detail)), detail.action_id);
    if (!outcome.ok) {
      setNotice(outcome.message);
      return "failed";
    }
    console.log("[FormProposal] saved", detail.action_id);
    setNotice(null);
    setTaken(true);
    onSaved();
    return "ok";
  }

  useSaveSection(proposed ? { label: "Formular", dirty: false, pending: true, save } : null);

  return proposed ? (
    <>
      <p className={NOTE_CLASS}>
        Vorgeschlagen sind Vor- und Nachname als Pflichtfelder. Gespeichert ist der Vorschlag noch
        nicht: »Speichern« in der Leiste übernimmt ihn.
      </p>
      <Hint text={notice} />
    </>
  ) : null;
}

/** Mode, transfer and, for a field of the action's own, type and choices of the chosen field. */
function FieldSettings({
  row,
  title,
  wording,
  onChange,
}: {
  readonly row: FieldRow;
  readonly title: string;
  /** »Sponsor« or »Kunde« (E8). */
  readonly wording: Wording;
  readonly onChange: (patch: (row: FieldRow) => Partial<FieldRow>) => void;
}): ReactElement {
  return (
    <>
      <Segmented
        name={id("abfrage", row.key)}
        legend={
          <>
            <InRow row={title} />
            Abfrage
          </>
        }
        options={modesFor(row.type)}
        labels={MODE_LABELS}
        value={row.mode}
        onChange={(mode) => {
          onChange(() => ({ mode }));
        }}
      />
      {row.type === null ? null : (
        <div className="sm:max-w-xs">
          <label className={LABEL_CLASS} htmlFor={id("art", row.key)}>
            <InRow row={title} />
            Art
          </label>
          <select
            id={id("art", row.key)}
            className={FIELD_CLASS}
            value={row.type}
            onChange={(event) => {
              const type = event.target.value as FieldType;
              onChange((current) => withType(current, type));
            }}
          >
            {Object.entries(TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      )}
      {row.type === "select" ? (
        <>
          <label className={LABEL_CLASS} htmlFor={id("auswahl", row.key)}>
            <InRow row={title} />
            Möglichkeiten, eine je Zeile (2 bis 20)
          </label>
          <textarea
            id={id("auswahl", row.key)}
            className={FIELD_CLASS}
            rows={4}
            value={row.options}
            onChange={(event) => {
              const options = event.target.value;
              onChange(() => ({ options }));
            }}
          />
        </>
      ) : null}
      <div className="mt-3 flex items-center gap-2">
        <input
          id={id("uebermitteln", row.key)}
          className="border border-input"
          type="checkbox"
          checked={row.transmit}
          onChange={(event) => {
            const transmit = event.target.checked;
            onChange(() => ({ transmit }));
          }}
        />
        <label className="text-sm" htmlFor={id("uebermitteln", row.key)}>
          <InRow row={title} />
          An {wording.oblique} übermitteln
        </label>
      </div>
      <Hint text={asksForAddress(row) ? addressHint(wording) : null} />
      <Hint text={asksForFreeText(row) ? FREE_TEXT_HINT : null} />
    </>
  );
}

const ACCOUNT_MODES = ["off", "on"] as const;
const ACCOUNT_LABELS = { off: "Aus", on: "Anbieten" } as const;

/**
 * The registration form of one action (D23, D35), as the view "Formular" of the design tab's
 * click editor (D52). Left a bar of its elements in the form's order, the keyboard's way: a name
 * chooses the element, its arrows move it. Under the bar the chosen element's settings and no
 * others; with nothing chosen, those of the page. Right the preview, `CompletionForm` of the
 * theme, the component of the participant page: a click chooses the element under it, its texts
 * and labels are edited in place, and a block is dragged by its handle. No category label stands
 * anywhere, the preview carries what participants read.
 *
 * **The server decides, the form shows.** One save sends the whole form, texts and consent
 * modes included, and takes the answer as the new state: a new field gets its key only there.
 * The consent wording in the preview is the server's rendering of the last save; whoever may
 * approve wording changes it in the element's settings, which approves a new version of the
 * cell's template at once, outside the bar (`ConsentWordingEditor`).
 *
 * Changeable until the first door opens (D24); afterwards the form only shows, with the reason.
 * The customer account stays changeable: its offer is no part of the form a consent quotes, and
 * it saves on its own (`/actions/account-offer`). The form saves with the page's bar: a typed
 * change counts as unsaved, the untouched proposal (first and last name) only as something to
 * save.
 */
export function FormEditor({
  detail,
  leadFields,
  accountOffer,
  onSaved,
  onInvalid,
}: {
  readonly detail: ActionDetail;
  /** Whether the form's fields, texts and consents may be changed (D57). */
  readonly leadFields: boolean;
  /** Whether the customer account offer may be changed (D57). */
  readonly accountOffer: boolean;
  /** After a save, for what else the form touches (the obstacles in the header). */
  readonly onSaved: () => void;
  /**
   * The bar stopped at this form, invalid or refused: the design tab brings its view into sight
   * (D49).
   */
  readonly onInvalid?: () => void;
}): ReactElement {
  const ended = detail.status === "finished" || detail.status === "archived";
  const locked = ended || !leadFields || !detail.lead_fields_editable;
  const titles = new Map(detail.core_lead_fields.map((field) => [field.key, field.label]));
  const [edited, setForm] = useState<RegistrationForm>(() => formOf(detail));
  const [saved, setSaved] = useState(detail);
  // What the server holds, as a request: the screen differs from it once something is typed.
  const [storedRequest, setStoredRequest] = useState(() => requestOf(formOf(detail)));
  // The chosen element, `null` for the page itself; and the chosen field of the block »Felder«.
  const [element, setElement] = useState<FormBlock | null>(null);
  const [chosenField, setChosenField] = useState<string | null>(null);
  const [accountOn, setAccountOn] = useState(detail.account_creation_enabled);
  // The kind whose wording was approved last: its editor takes the focus when it mounts anew.
  const [approved, setApproved] = useState<ConsentType | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const added = useRef(0);
  const focusNext = useRef<string | null>(null);
  // An element inside the preview frame: ids there resolve in the frame's own document.
  const inFrame = useRef<HTMLElement>(null);
  // Locked, the page shows what is stored: no proposal, and no edit from before the lock.
  const form = locked ? formOf(detail, false) : edited;
  const request = requestOf(withDefaultLabels(form, titles));
  // Only a tenant admin may approve wording; for anyone else this answers 403 and the
  // panel shows no editor.
  const templates = useResource<ConsentTemplate[]>(loadConsentTemplates);
  const dragged = useRef<FormBlock | null>(null);
  const [dragging, setDragging] = useState(false);
  const draggedField = useRef<string | null>(null);
  const [draggingField, setDraggingField] = useState(false);
  const identity = detail.oidc_issuer !== null;

  // After every render: the focus a row operation or a refusal asked for, in this page or in
  // the frame. The save makes the texts read-only, so it waits until the save is over.
  useEffect(() => {
    const target = focusNext.current;
    if (target !== null && !busy) {
      focusNext.current = null;
      (
        document.getElementById(target) ?? inFrame.current?.ownerDocument.getElementById(target)
      )?.focus();
    }
  });

  // A click in the preview brings the settings into view on a narrow screen, where they stand
  // above the preview; a click in the bar is already beside them.
  const heading = useRef<HTMLHeadingElement>(null);
  const fromPreview = useRef(false);
  useEffect(() => {
    if (fromPreview.current) {
      fromPreview.current = false;
      const node = heading.current;
      // jsdom has no scrollIntoView.
      if (node !== null && "scrollIntoView" in node) {
        node.scrollIntoView({ block: "nearest" });
      }
    }
  }, [element, chosenField]);

  const focus = (target: string): void => {
    focusNext.current = target;
  };
  const setFields = (change: (fields: readonly FieldRow[]) => readonly FieldRow[]): void => {
    setForm((current) => ({ ...current, fields: change(current.fields) }));
  };
  const update = (key: string, patch: (row: FieldRow) => Partial<FieldRow>): void => {
    setFields((fields) => fields.map((row) => (row.key === key ? { ...row, ...patch(row) } : row)));
  };
  const titleOf = (row: FieldRow): string =>
    row.type === null
      ? row.label.trim() || (titles.get(row.key) ?? row.key)
      : row.label.trim() || "Eigenes Feld";
  const chooseField = (key: string): void => {
    setElement("lead_fields");
    setChosenField(key);
  };

  /**
   * Moves `entry` one place among `shown`, the parts the preview shows, onto the place of its
   * neighbour. The focus stays on the entry's handle, which moves with it.
   */
  const step = <T extends string>(
    shown: readonly T[],
    entry: T,
    by: -1 | 1,
    write: (target: T) => void,
  ): void => {
    const target = shown[shown.indexOf(entry) + by];
    if (target === undefined) {
      return;
    }
    focus(id("griff", entry));
    write(target);
  };
  const moveField = (key: string, target: string): void => {
    setFields((fields) => {
      const order = movedBlock(
        fields.map((entry) => entry.key),
        key,
        target,
      );
      return order.map((entry) => fields.find((row) => row.key === entry)!);
    });
  };

  async function save(): Promise<SaveResult> {
    const unnamed = form.fields.find((row) => row.type !== null && row.label.trim() === "");
    if (unnamed !== undefined) {
      // Switched off, it stands only as a chip: back into the form, where its label is typed.
      if (unnamed.mode === "hidden") {
        update(unnamed.key, () => ({ mode: "optional" }));
      }
      chooseField(unnamed.key);
      setNotice("Jedes eigene Feld braucht eine Beschriftung. Bitte in der Vorschau eintragen.");
      focus(id("beschriftung", unnamed.key));
      onInvalid?.();
      return "invalid";
    }
    setBusy(true);
    setNotice(null);
    try {
      const outcome = await saveLeadFields(request, detail.action_id);
      if (!outcome.ok) {
        setNotice(outcome.message);
        onInvalid?.();
        return "failed";
      }
      console.log("[FormEditor] saved", detail.action_id);
      const next = formOf(outcome.detail);
      setForm(next);
      setSaved(outcome.detail);
      setStoredRequest(requestOf(next));
      // A new field came back with the server's key; its stand-in names nothing any more.
      setChosenField((current) => (next.fields.some((row) => row.key === current) ? current : null));
      onSaved();
      return "ok";
    } catch (error: unknown) {
      console.error("[FormEditor]", error);
      setNotice(LOAD_FAILED);
      return "failed";
    } finally {
      setBusy(false);
    }
  }

  /**
   * After a new approved wording: the preview's consents as the server now renders them. Only
   * `saved` is replaced, the server's state; what is typed into the form stays as it is. The
   * focus goes back to the wording field when its new version mounts (`focusOnMount`).
   */
  async function refreshPreview(kind: ConsentType): Promise<void> {
    setNotice(
      `Neue Fassung der Einwilligung ${CONSENT_TYPE_LABELS[kind]} freigegeben. Sie gilt für alle Aktionen.`,
    );
    try {
      const { data } = await loadActionDetail(client, detail.action_id);
      if (data !== undefined) {
        setSaved(data);
      }
    } catch (error: unknown) {
      console.error("[FormEditor]", error);
      setNotice(LOAD_FAILED);
    }
  }

  useSaveSection(
    locked
      ? null
      : {
          label: "Formular",
          dirty: !sameValue(request, storedRequest),
          pending: form.proposed,
          save,
        },
  );

  const text = (key: FormTextKey, fallback: string, multiline = false): ReactElement => {
    const { field, label, max } = TEXTS[key];
    return (
      <EditableText
        id={id(key)}
        value={form[field]}
        onChange={(value) => {
          setForm((current) => ({ ...current, [field]: value }));
        }}
        label={label}
        placeholder={fallback === "" ? label : fallback}
        placeholderTone={fallback === "" ? "hint" : "page"}
        multiline={multiline}
        maxLength={max}
        readOnly={locked || busy}
      />
    );
  };

  const asked = form.fields.filter((row) => row.mode !== "hidden");
  const hidden = form.fields.filter((row) => row.mode === "hidden");
  const field = asked.find((row) => row.key === chosenField);
  // The elements the bar lists: those with settings. The address and the terms have only their
  // texts, typed in the preview; the account offer only where an identity service keeps it.
  const listed = form.blockOrder.filter(
    (block) => block !== "email" && block !== "terms" && (block !== "account" || identity),
  );
  const off = (block: FormBlock): boolean => {
    const kind = consentOf(block);
    return block === "account" ? !accountOn : kind !== null && form.consentModes[kind] === "hidden";
  };
  const choose = (next: FormBlock | null): void => {
    setElement((current) => (current === next ? null : next));
  };

  const edit: ParticipantEdit = {
    text,
    fields: locked
      ? undefined
      : {
          label: (key) => {
            const row = form.fields.find((entry) => entry.key === key);
            return row === undefined ? null : (
              <EditableText
                id={id("beschriftung", key)}
                value={row.label}
                onChange={(label) => {
                  update(key, () => ({ label }));
                }}
                label={`Beschriftung ${titleOf(row)}`}
                placeholder={row.type === null ? (titles.get(key) ?? key) : "Beschriftung"}
                placeholderTone={row.type === null ? "page" : "hint"}
                maxLength={200}
                readOnly={busy}
              />
            );
          },
          item: (key) => ({
            className: element === "lead_fields" && chosenField === key ? CHOSEN_CLASS : CHOOSABLE_CLASS,
            onSelect: () => {
              chooseField(key);
            },
          }),
          frame: (key, content, shown) => {
            const row = form.fields.find((entry) => entry.key === key);
            return (
              <Movable
                kind="field"
                name={row === undefined ? key : titleOf(row)}
                handle={id("griff", key)}
                chosen={element === "lead_fields" && chosenField === key}
                dragging={draggingField}
                disabled={busy}
                onDragStart={() => {
                  draggedField.current = key;
                  setDraggingField(true);
                }}
                onDragEnd={() => {
                  draggedField.current = null;
                  setDraggingField(false);
                }}
                onDrop={() => {
                  const source = draggedField.current;
                  draggedField.current = null;
                  setDraggingField(false);
                  if (source !== null && source !== key) {
                    moveField(source, key);
                  }
                }}
                onMove={(by) => {
                  step(shown, key, by, (target) => {
                    moveField(key, target);
                  });
                }}
              >
                {content}
              </Movable>
            );
          },
        },
    block: locked
      ? undefined
      : (block, content, shown) => (
          <Movable
            kind="block"
            block={block}
            name={BLOCK_LABELS[block]}
            handle={id("griff", block)}
            chosen={element === block && (block !== "lead_fields" || field === undefined)}
            dragging={dragging}
            disabled={busy}
            onDragStart={() => {
              dragged.current = block;
              setDragging(true);
            }}
            onDragEnd={() => {
              dragged.current = null;
              setDragging(false);
            }}
            onDrop={() => {
              const source = dragged.current;
              dragged.current = null;
              setDragging(false);
              if (source !== null && source !== block) {
                setForm((current) => ({
                  ...current,
                  blockOrder: movedBlock(current.blockOrder, source, block),
                }));
              }
            }}
            onMove={(by) => {
              step(shown, block, by, (target) => {
                setForm((current) => ({
                  ...current,
                  blockOrder: movedBlock(current.blockOrder, block, target),
                }));
              });
            }}
          >
            {content}
          </Movable>
        ),
  };

  const preview = (
    <>
      <p className="mb-2 text-sm text-muted-foreground">
        {locked
          ? "So sehen Teilnehmende das Formular."
          : "Vorschau: Klicken Sie auf ein Element, um es zu wählen."}
      </p>
      <ParticipantFrame title="Vorschau des Teilnahmeformulars">
        {/* The card of the page (abschluss.astro): the door's prize on top, then the form. A
            click on a block chooses it, one beside the blocks the page itself. */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events -- the mouse's way to an element; the keyboard chooses it in the bar */}
        <section
          ref={inFrame}
          className="countdown-panel"
          onClick={
            locked
              ? undefined
              : (event) => {
                  // The target comes from the frame's window, where `instanceof` of this one fails.
                  const target = event.target as Partial<Element> | null;
                  const hit =
                    typeof target?.closest === "function"
                      ? target.closest<HTMLElement>("[data-block]")
                      : null;
                  const block = (hit?.dataset.block as FormBlock | undefined) ?? null;
                  // The address and the terms have no settings beside their texts.
                  const next = block === "email" || block === "terms" ? null : block;
                  fromPreview.current = next !== element;
                  setElement(next);
                }
          }
        >
          <div className="countdown-prize">
            <p className="countdown-subheading">Gewinn des Türchens</p>
          </div>
          <CompletionForm
            bare
            action="#"
            fieldPosition={0}
            termsVersionId=""
            termsHref="#"
            privacyHref={detail.privacy_policy_url}
            signedIn={false}
            deviceRecognised={false}
            headline={form.headline || null}
            intro={form.intro || null}
            submitLabel={form.submitLabel || null}
            blockOrder={form.blockOrder}
            fields={previewFields(form, detail.core_lead_fields)}
            reusedOn={null}
            consents={saved.consent_preview
              .filter((offer) => form.consentModes[offer.consent_type] !== "hidden")
              .map((offer) => ({
                ...offer,
                mandatory: form.consentModes[offer.consent_type] === "required",
              }))}
            accountOffered={accountOn && identity}
            typed={{}}
            problems={{}}
            edit={edit}
          />
        </section>
      </ParticipantFrame>
    </>
  );

  const consentPanel = (kind: ConsentType): ReactElement => {
    const mode = form.consentModes[kind];
    const template =
      templates.status === "ready"
        ? templates.data.find(
            (entry) =>
              entry.consent_type === kind &&
              entry.body_template !== null &&
              entry.body_template !== undefined,
          )
        : undefined;
    return (
      <div key={kind} hidden={element !== kind}>
        <fieldset disabled={locked || busy} className="min-w-0">
          <Segmented
            name={id("abfrage", kind)}
            legend="Abfrage"
            options={modesFor(null)}
            labels={MODE_LABELS}
            value={mode}
            onChange={(next: Mode) => {
              setForm((current) => ({
                ...current,
                consentModes: { ...current.consentModes, [kind]: next },
              }));
            }}
            after={
              // An advertising consent as a condition of taking part (D51): the terms must say so.
              mode === "required" && kind !== "registration" ? (
                <HelpTip topic="Pflicht-Einwilligung">{HELP_TIPS.consentCoupling}</HelpTip>
              ) : null
            }
          />
        </fieldset>
        {mode === "hidden" || saved.consent_preview.some((offer) => offer.consent_type === kind) ? null : (
          // The server leaves a kind out of the preview without approved wording, and also while
          // a draft lacks the organizer or privacy link its wording names (`{controller}`).
          <p className={NOTE_CLASS}>{missingWording(templates.status === "ready", template !== undefined)}</p>
        )}
        {template === undefined ? null : (
          <ConsentWordingEditor
            key={String(template.version)}
            kind={kind}
            template={template}
            // The new version mounts the editor anew, whenever the template list comes back.
            focusOnMount={approved === kind}
            onApproved={() => {
              setApproved(kind);
              templates.reload();
              void refreshPreview(kind);
            }}
          />
        )}
      </div>
    );
  };

  return (
    <EditorLayout preview={preview} previewWide>
      <nav aria-label="Elemente des Formulars">
        <ol className="flex flex-wrap gap-2">
          {listed.map((block) => (
            <Entry
              key={block}
              entry={block}
              name={BLOCK_LABELS[block]}
              off={off(block)}
              chosen={element === block}
              onChoose={() => {
                choose(block);
              }}
            />
          ))}
        </ol>
      </nav>

      {element === null ? null : (
        <h3 ref={heading} className="mt-6 text-base font-semibold">
          {BLOCK_LABELS[element]}
        </h3>
      )}
      {detail.lead_fields_editable ? (
        ended ? (
          <p className={NOTE_CLASS}>Die Aktion ist beendet. Das Formular bleibt, wie es ist.</p>
        ) : null
      ) : (
        <p className={NOTE_CLASS}>
          Gesperrt, seit das erste Türchen geöffnet hat. Änderungen nur noch auf dem Notweg über
          die IT-Leitung.
        </p>
      )}
      <div role="status">
        {notice === null ? null : <p className={`${NOTE_CLASS} whitespace-pre-line`}>{notice}</p>}
      </div>

      {/* The page itself, while no element is chosen. */}
      <fieldset disabled={locked || busy} hidden={element !== null} className="min-w-0">
        <div className="sm:max-w-sm">
          <label className={LABEL_CLASS} htmlFor={id("title")}>
            Titel im Browser-Tab
          </label>
          <input
            id={id("title")}
            className={FIELD_CLASS}
            value={form.title}
            maxLength={TEXTS.title.max}
            placeholder={PAGE_DEFAULT}
            onChange={(event) => {
              const title = event.target.value;
              setForm((current) => ({ ...current, title }));
            }}
          />
        </div>
        {form.kept.length === 0 ? null : (
          <>
            <h4 className="mt-6 text-sm font-semibold">Weitere Felder</h4>
            <p className="mt-1 text-sm text-muted-foreground">
              Aus einer früheren Einrichtung, nicht im Formular.
            </p>
            <ul className="mt-2 space-y-2">
              {form.kept.map((kept) => (
                <li
                  key={kept.key}
                  className="flex items-center gap-3 border-l-2 border-border pl-3 text-sm"
                >
                  {kept.label}
                  {locked ? null : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      aria-label={`${kept.label} entfernen`}
                      onClick={() => {
                        focus(id("title"));
                        setForm((current) => ({
                          ...current,
                          kept: current.kept.filter((entry) => entry.key !== kept.key),
                        }));
                      }}
                    >
                      Entfernen
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </fieldset>

      {/* The fields: the chosen one's settings, chosen and moved in the preview; switched-off
          ones as chips. The address and the terms have no panel: their texts are typed in the
          preview, and both are always asked. */}
      <div hidden={element !== "lead_fields"}>
        <fieldset disabled={locked || busy} className="min-w-0">
          {field === undefined ? null : (
            <div className="mt-4 border-l-2 border-primary pl-3">
              <h4 className="text-sm font-semibold">{titleOf(field)}</h4>
              <FieldSettings
                row={field}
                title={titleOf(field)}
                wording={wordingOf(detail)}
                onChange={(patch) => {
                  if (patch(field).mode === "hidden") {
                    focus(id("hinzufuegen", field.key));
                  }
                  update(field.key, patch);
                }}
              />
              {field.type === null ? null : (
                <Button
                  id={id("entfernen", field.key)}
                  className="mt-4"
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-label={`${titleOf(field)} entfernen`}
                  onClick={() => {
                    focus(id("feld-neu"));
                    setChosenField(null);
                    setFields((fields) => fields.filter((entry) => entry.key !== field.key));
                  }}
                >
                  Entfernen
                </Button>
              )}
            </div>
          )}
          {locked ? null : (
            <div className="mt-4 flex flex-wrap gap-2">
              {hidden.map((row) => (
                <Button
                  key={row.key}
                  id={id("hinzufuegen", row.key)}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-dashed"
                  aria-label={`${titleOf(row)} hinzufügen`}
                  onClick={() => {
                    update(row.key, () => ({ mode: "optional" }));
                    setChosenField(row.key);
                    focus(id("beschriftung", row.key));
                  }}
                >
                  + {titleOf(row)}
                </Button>
              ))}
              <Button
                id={id("feld-neu")}
                type="button"
                variant="outline"
                size="sm"
                className="border-dashed"
                aria-label="Eigenes Feld hinzufügen"
                onClick={() => {
                  added.current += 1;
                  const row = newField(added.current);
                  focus(id("beschriftung", row.key));
                  setFields((fields) => [...fields, row]);
                  setChosenField(row.key);
                }}
              >
                + Feld
              </Button>
            </div>
          )}
        </fieldset>
      </div>

      {(["registration", "newsletter", "sponsor_contact", "daily_reminder"] as const).map(
        consentPanel,
      )}

      {/* Outside the lock (D24): the offer is no part of what a consent quotes. */}
      {identity ? (
        <SaveForm
          label="Kundenkonto"
          readOnly={ended || !accountOffer}
          hidden={element !== "account"}
          onSaved={onSaved}
          submit={(data) =>
            saveActionSection(
              "account-offer",
              { enabled: data.get("konto_angebot") === "on" },
              detail.action_id,
            )
          }
        >
          <Segmented
            name="konto_angebot"
            legend="Angebot"
            options={ACCOUNT_MODES}
            labels={ACCOUNT_LABELS}
            value={accountOn ? "on" : "off"}
            onChange={(next) => {
              setAccountOn(next === "on");
            }}
          />
        </SaveForm>
      ) : null}
    </EditorLayout>
  );
}
